<template>
  <CityAnchorNav
    :visible="showAnchorNav"
    :cities="nearbyCitiesList"
    :active-key="activeCityKey"
    :analytics-base="analyticsParams"
    @select="scrollToCity"
  />

  <NotFoundBlock
    v-if="nearbyNotFound"
    :api-url="apiUrl"
    :sogu-url="soguServiceUrl"
  />

  <template v-else-if="isContentLoading">
    <BreadcrumbsSkeleton :animated="isContentLoading" />

    <MosaicSkeleton :animated="isContentLoading" />

    <section
      v-for="n in 2"
      :key="`city-skeleton-${n}`"
      class="city-block"
    >
      <PageWrap>
        <InfoSkeleton :animated="isContentLoading" />
      </PageWrap>
      <AttractionsSkeleton :animated="isContentLoading" />
      <ExperienceSliderSkeleton :animated="isContentLoading" />
    </section>
  </template>

  <template v-else>
    <AroundBreadcrumbs :list="breadcrumbs" />

    <section class="mosaic-block">
      <PageWrap>
        <div ref="pageHeadingRef">
          <AroundPageHeading
            :title="pageH1"
            :analytics-base="analyticsParams"
            @change-city="openCitySelect"
          />
        </div>

        <MosaicGrid
          v-if="mosaicItems.length"
          :items="mosaicItems"
          class="mosaic"
        >
          <template #default="{ item }">
            <MosaicCard
              :title="item.title"
              :name="item.name"
              :position="item.position"
              :photo="item.photo"
              @select="scrollToCity(item.city)"
            />
          </template>
        </MosaicGrid>
      </PageWrap>
    </section>

    <template
      v-for="(city, idx) in nearbyCitiesList"
      :key="city.url || idx"
    >
      <section
        v-if="city.description_long || city.attractions?.length || hasExperiences(city)"
        :id="cityAnchorId(city)"
        :data-city-key="cityKey(city)"
        class="city-block"
      >
        <PageWrap>
          <CityInfo
            :title="city.name"
            :description="city.description_long"
          />
        </PageWrap>

        <AttractionsSection
          v-if="city.attractions?.length"
          :attractions="city.attractions"
          :analytics-base="getSightSnippetAnalytics(city)"
          :sights-url="getAttractionsUrl(city)"
          :title="`Что посмотреть ${city.in_obj_phrase || `в ${city.name_prepositional || city.name}`}`"
          @select="(attraction) => openSightModal(attraction, city)"
        />

        <ExperienceSlider
          v-if="hasExperiences(city)"
          :title="`Экскурсии ${city.in_obj_phrase}`"
          :from-experiences="asExperiences(city.city_from_experiences)"
          :to-experiences="asExperiences(city.city_to_experiences)"
          :from-label="rootInObjPhrase ? `Старт ${rootInObjPhrase}` : ''"
          :to-label="city.in_obj_phrase ? `Старт ${city.in_obj_phrase}` : ''"
          :show-all-url="city.url"
          :from-show-all-url="city.city_from_experiences_link"
          :to-show-all-url="city.city_to_experiences_link"
          :analytics-base="getSightSnippetAnalytics(city)"
        />
      </section>
    </template>
  </template>

  <SightDetailsModal
    v-if="activeSight"
    :open="isSightModalOpen"
    :teleport-target="modalTarget"
    :city="activeSightCity"
    :sight="activeSight"
    :modal-nav="sightModalNav"
    :guide-comments="sightGuideComments"
    :guide-share="sightGuideShare"
    :has-more-guide-comments="hasMoreSightGuideComments"
    :analytics-base="sightAnalyticsBase"
    @close="closeSightModal"
    @show-more-guide-comments="onShowMoreGuideComments"
  >
    <template #experience>
      <SightExperience
        :experiences="sightExperiences"
        :total-count="activeSight?.excursions_count ?? sightExperiences.length"
        :show-all-url="showAllSightExperiencesUrl"
        :has-more="sightExperiences.length > 0"
        :analytics-base="sightAnalyticsBase"
      />
    </template>
  </SightDetailsModal>

  <CitySelectModal
    :open="isCitySelectOpen"
    :teleport-target="modalTarget"
    :analytics-base="analyticsParams"
    @close="closeCitySelect"
    @select="onCitySelect"
  />

  <SubscriptionBlock
    v-if="!nearbyNotFound"
    :api-url="``"
    :analytics-api-url="soguServiceUrl"
  />

  <PageWrap
    v-if="!nearbyNotFound"
    class="seo-block-wrap"
  >
    <SeoExperienceBlock
      :default-sogu-fields="seoDefaultSoguFields"
      :text="seoText"
      :faq="seoFaq"
      current-tag="all"
      :slug="seoCitySlug"
      :city-info-name-ru="seoCityNameRu"
      :is-guides-link-shown="true"
      :is-attraction-link-shown="true"
      :is-reviews-link-shown="true"
      :slug-info="seoSlugInfo"
      :tags="seoTags"
      :route="route"
    />
  </PageWrap>
</template>

<script setup lang="ts">
  import { NotFoundBlock } from '@experience/site-404-package';
  import { SubscriptionBlock } from '@experience/subscription-block-package';
  import { SeoExperienceBlock } from '@experience/travelers-seo-block-package';
  import { useHead } from '@unhead/vue';
  import { computed, inject, nextTick, onMounted, onUnmounted, ref, watch, type useShadowRoot } from 'vue';
  import { useRoute } from 'vue-router';

  import AroundBreadcrumbs from '@/entities/aroundV2/AroundBreadcrumbs.vue';
  import AroundPageHeading from '@/entities/aroundV2/AroundPageHeading.vue';
  import AttractionsSection from '@/entities/aroundV2/AttractionsSection.vue';
  import CityAnchorNav from '@/entities/aroundV2/CityAnchorNav.vue';
  import CityInfo from '@/entities/aroundV2/CityInfo.vue';
  import ExperienceSlider from '@/entities/aroundV2/ExperienceSlider.vue';
  import MosaicCard from '@/entities/aroundV2/MosaicCard.vue';
  import AttractionsSkeleton from '@/entities/aroundV2/skeletons/AttractionsSkeleton.vue';
  import BreadcrumbsSkeleton from '@/entities/aroundV2/skeletons/BreadcrumbsSkeleton.vue';
  import ExperienceSliderSkeleton from '@/entities/aroundV2/skeletons/ExperienceSliderSkeleton.vue';
  import InfoSkeleton from '@/entities/aroundV2/skeletons/InfoSkeleton.vue';
  import MosaicSkeleton from '@/entities/aroundV2/skeletons/MosaicSkeleton.vue';
  import SightExperience from '@/features/sights/SightExperience.vue';
  import type {
    City,
    CitySuggest,
    CityTag as ApiCityTag,
    CitiesAroundSeo,
    NearbyAttraction,
    NearbyCities,
    NearbyCity,
    NearbyExperience,
  } from '@/shared/api/generated/api';
  import {
    getAttractionComments,
    getCityDetail,
    getCityTags,
    getCitiesAroundSeo,
    getExperiences,
    getNearbyCities,
  } from '@/shared/api/generated/api';
  import { getRuntimeConfig } from '@/shared/config/runtime';
  import { trackEvent } from '@/shared/lib/analytics/compat';
  import { useRenderReport } from '@/shared/lib/lifecycle';
  import { makeFullLink } from '@/shared/lib/makeFullLink';
  import { defaultSightModalNav } from '@/shared/types/sight-modal';
  import type { SightGuideComment, SightModalCity, SightModalNav, SightModalSight } from '@/shared/types/sight-modal';
  import MosaicGrid from '@/shared/ui/MosaicGrid.vue';
  import PageWrap from '@/shared/ui/PageWrap.vue';
  import CitySelectModal from '@/widgets/CitySelectModal.vue';
  import SightDetailsModal from '@/widgets/SightDetailsModal.vue';

  import type { IUiBreadcrumbItem } from '@experience/core-breadcrumbs-ui';
  import type { Experience } from '@experience/exp-card/dist/types/experience';
  import type { CityInfo as SeoCityInfo, CityTag } from '@experience/travelers-seo-block-package/dist/types/city';
  import type { ContextGeoSubject } from '@experience/travelers-seo-block-package/dist/types/geo';
  import type { SeoDataFaq } from '@experience/travelers-seo-block-package/dist/types/seoData';

  const { VITE_RUNTIME_SOGU_SERVICE_URL: soguServiceUrl } = getRuntimeConfig();
  const apiUrl = import.meta.env.VITE_API_URL;
  const route = useRoute();

  useRenderReport();

  const cityDetail = ref<City | null>(null);

  const analyticsParams = computed(() => ({
    city_id: cityDetail.value?.id ?? 0,
    city_name: cityDetail.value?.name_ru ?? '',
    country_id: cityDetail.value?.country.id ?? 0,
    country_name: cityDetail.value?.country.name_ru ?? '',
  }));

  const getSightSnippetAnalytics = (city: NearbyCity): Record<string, unknown> => ({
    ...analyticsParams.value,
    subject_geo_name: city.name,
    subject_geo_type: 'city',
  });

  // Базовые параметры аналитики модалки достопримечательности.
  // В city_* передаём город раздела (город рядом, из чьего раздела открыта модалка),
  // а не город страницы. У NearbyCity нет id, поэтому city_id не заполняем; страна — от страницы.
  const sightAnalyticsBase = computed(() => ({
    ...analyticsParams.value,
    city_id: undefined,
    city_name: activeSightCity.value.name_ru,
    tag_id: activeSight.value?.tag_id,
    tag_name: activeSight.value?.name,
  }));

  const isSightModalOpen = ref(false);
  const isCitySelectOpen = ref(false);
  const modalTarget = ref<HTMLElement | null>(null);
  const defaultSightCity: SightModalCity = { name_ru: 'Москва', slug: 'Moscow' };

  const activeSight = ref<SightModalSight | null>(null);
  const activeSightCity = ref<SightModalCity>(defaultSightCity);
  const activeSightShowAllUrl = ref('');
  const sightExperiences = ref<Experience[]>([]);
  const sightModalNav = ref<SightModalNav>(defaultSightModalNav());
  const sightGuideComments = ref<SightGuideComment[]>([]);
  const sightGuideShare = ref<SightGuideComment | null>(null);
  const hasMoreSightGuideComments = ref(false);

  const SIGHT_EXPERIENCES_LIMIT = 6;
  let sightExperiencesRequestId = 0;

  const SIGHT_COMMENTS_PAGE_SIZE = 3;
  const sightCommentsPage = ref(1);
  let sightCommentsRequestId = 0;

  const rootNode = inject<ReturnType<typeof useShadowRoot>>('shadowRoot', null);

  if (rootNode) {
    modalTarget.value = rootNode.querySelector('.modal-target');
  } else if (typeof document !== 'undefined') {
    modalTarget.value = document.querySelector('.modal-target');
  }

  const extractCitySlug = (url?: string): string => {
    if (!url) return '';

    return url.match(/\/experience\/([^/]+)/)?.[1] ?? '';
  };

  const cityAnchorId = (city: NearbyCity): string =>
    `around-city-${extractCitySlug(city.url) || city.name.replace(/\s+/g, '-')}`;

  const getAttractionsUrl = (city: NearbyCity): string => {
    const slug = extractCitySlug(city.url);

    return slug ? `/attractions/${slug}/` : '';
  };

  const scrollToCity = (city: NearbyCity) => {
    const scope: Document | ShadowRoot | null = rootNode ?? (typeof document !== 'undefined' ? document : null);
    const el = scope?.getElementById(cityAnchorId(city));

    el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const cityKey = (city: NearbyCity): string => city.url || city.name || '';

  const loadSightExperiences = async (tagId?: number, sightCitySlug?: string) => {
    if (!tagId) return;

    const requestId = ++sightExperiencesRequestId;

    try {
      const { data } = await getExperiences({
        query: {
          tags: tagId,
          city__slug: sightCitySlug || undefined,
          limit: SIGHT_EXPERIENCES_LIMIT,
          offset: 0,
          price_format: 'travelers',
        },
      });

      if (requestId !== sightExperiencesRequestId) return;

      const results = data?.results ?? [];

      sightExperiences.value = asExperiences(results);

      if (activeSight.value && !activeSight.value.excursions_count && data?.count) {
        activeSight.value = { ...activeSight.value, excursions_count: data.count };
      }

      sightModalNav.value = {
        ...sightModalNav.value,
        experience: sightExperiences.value.length > 0,
      };
    } catch {
      if (requestId === sightExperiencesRequestId) {
        sightExperiences.value = [];
      }
    }
  };

  const loadSightComments = async (attractionId?: number, page = 1, append = false) => {
    if (!attractionId) return;

    const requestId = ++sightCommentsRequestId;

    try {
      const { data } = await getAttractionComments({
        path: { id: attractionId },
        query: { page, page_size: SIGHT_COMMENTS_PAGE_SIZE },
      });

      if (requestId !== sightCommentsRequestId) return;

      const results = (data?.results ?? []) as unknown as SightGuideComment[];

      sightGuideComments.value = append ? [...sightGuideComments.value, ...results] : results;
      sightCommentsPage.value = page;
      hasMoreSightGuideComments.value = Boolean(data?.next);

      sightModalNav.value = {
        ...sightModalNav.value,
        guides: sightGuideComments.value.length > 0,
      };
    } catch {
      if (!append && requestId === sightCommentsRequestId) {
        sightGuideComments.value = [];
        hasMoreSightGuideComments.value = false;
      }
    }
  };

  const openSightModal = (attraction: NearbyAttraction, city?: NearbyCity) => {
    activeSight.value = {
      id: attraction.id,
      name: attraction.name,
      slug: attraction.slug,
      tag_id: attraction.tag_id,
      description: attraction.description,
      address: attraction.address,
      latitude: attraction.latitude,
      longitude: attraction.longitude,
      excursions_count: attraction.excursions_count,
      experience_main_photo_url: attraction.experience_main_photo_url ?? '',
    };

    activeSightCity.value = city ? { name_ru: city.name, slug: extractCitySlug(city.url) } : defaultSightCity;

    activeSightShowAllUrl.value = attraction.citytag_url ?? '';

    sightExperiences.value = [];
    sightGuideComments.value = [];
    sightGuideShare.value = null;
    hasMoreSightGuideComments.value = false;
    sightCommentsPage.value = 1;

    sightModalNav.value = {
      info: Boolean(activeSight.value.description),
      position: Boolean(activeSight.value.latitude && activeSight.value.longitude),
      guides: false,
      experience: false,
    };

    isSightModalOpen.value = true;

    void trackEvent('around:sights:sight-popup.shown', sightAnalyticsBase.value);

    void loadSightExperiences(attraction.tag_id, activeSightCity.value.slug);
    void loadSightComments(attraction.id, 1, false);
  };

  const closeSightModal = () => {
    void trackEvent('around:sights:sight-popup.close', sightAnalyticsBase.value);
    isSightModalOpen.value = false;
  };

  const openCitySelect = () => {
    isCitySelectOpen.value = true;
  };

  const closeCitySelect = () => {
    isCitySelectOpen.value = false;
  };

  const onCitySelect = (city: CitySuggest) => {
    closeCitySelect();

    const slug = city.asciiName || extractCitySlug(city.url ?? '');

    if (!slug) return;

    window.location.assign(makeFullLink(`/around/${slug}`));
  };

  const onShowMoreGuideComments = () => {
    void loadSightComments(activeSight.value?.id, sightCommentsPage.value + 1, true);
  };

  const getCurrentCitySlugFromUrl = () => {
    if (typeof window === 'undefined') return '';

    const match = window.location.pathname.match(/^\/experience\/([^/]+)/);

    return match?.[1] ?? '';
  };

  const nearbyLoading = ref(false);
  const nearbyError = ref<string | null>(null);
  const nearbyData = ref<NearbyCities | null>(null);
  const pageSeoData = ref<CitiesAroundSeo | null>(null);
  const cityTagsData = ref<ApiCityTag[]>([]);
  // Если BFF nearby-cities вернул не 200 — показываем шаблон 404 вместо контента страницы
  const nearbyNotFound = ref(false);

  const nearbyCitiesList = computed(() => nearbyData.value?.nearby_cities ?? []);

  const rootInObjPhrase = computed(() => nearbyData.value?.in_obj_phrase ?? '');

  const isContentLoading = computed(() => nearbyLoading.value || (!nearbyData.value && !nearbyError.value));

  // Прилипающая якорная навигация по городам (появляется, когда заголовок страницы ушёл с экрана)
  const pageHeadingRef = ref<HTMLElement | null>(null);
  const isPageHeadingVisible = ref(true);
  const activeCityKey = ref('');

  const showAnchorNav = computed(
    () => !isContentLoading.value && nearbyCitiesList.value.length > 1 && !isPageHeadingVisible.value,
  );

  let pageHeadingObserver: IntersectionObserver | null = null;
  let sectionObserver: IntersectionObserver | null = null;

  const teardownObservers = () => {
    pageHeadingObserver?.disconnect();
    pageHeadingObserver = null;
    sectionObserver?.disconnect();
    sectionObserver = null;
  };

  const setupObservers = () => {
    if (typeof window === 'undefined' || !('IntersectionObserver' in window)) return;

    teardownObservers();

    const pageHeadingEl = pageHeadingRef.value;

    if (pageHeadingEl) {
      pageHeadingObserver = new IntersectionObserver(
        ([entry]) => {
          if (entry) isPageHeadingVisible.value = entry.isIntersecting;
        },
        { threshold: 0 },
      );
      pageHeadingObserver.observe(pageHeadingEl);
    }

    const scope: Document | ShadowRoot | null = rootNode ?? (typeof document !== 'undefined' ? document : null);
    const sections = scope ? Array.from(scope.querySelectorAll<HTMLElement>('.city-block')) : [];

    if (sections.length) {
      sectionObserver = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              activeCityKey.value = (entry.target as HTMLElement).dataset.cityKey ?? '';
            }
          });
        },
        { rootMargin: '-45% 0px -50% 0px', threshold: 0 },
      );
      sections.forEach((el) => sectionObserver?.observe(el));
    }
  };

  watch(
    [isContentLoading, () => nearbyCitiesList.value.length],
    ([loading]) => {
      if (!loading) {
        void nextTick(setupObservers);
      }
    },
    { flush: 'post' },
  );

  onUnmounted(teardownObservers);

  const asExperiences = (list?: NearbyExperience[]): Experience[] => (list ?? []) as unknown as Experience[];

  const hasExperiences = (city: NearbyCity): boolean =>
    Boolean(city.city_from_experiences?.length || city.city_to_experiences?.length);

  const mosaicItems = computed(() =>
    nearbyCitiesList.value.map((city) => ({
      title: city.description_short || city.name,
      name: city.name,
      position: typeof city.distance_km === 'number' ? `${String(city.distance_km)} км` : '',
      photo: city.image_url || '',
      url: city.url || '',
      city,
    })),
  );

  const citySlug = computed(
    () => (route.params.slug as string) || getCurrentCitySlugFromUrl() || defaultSightCity.slug || 'moscow',
  );

  // Хлебные крошки: Главная → Город → Название текущего раздела (некликабельно).
  const breadcrumbs = computed<IUiBreadcrumbItem[]>(() => {
    const items: IUiBreadcrumbItem[] = [{ label: 'Главная', to: '/' }];

    const cityName = cityDetail.value?.name_ru;

    if (cityName) {
      items.push({ label: cityName, to: `/experience/${citySlug.value}/` });
    }

    const sectionTitle = pageH1.value;

    if (sectionTitle) {
      items.push({ label: sectionTitle, to: '' });
    }

    return items;
  });

  const pageH1 = computed(() => {
    const seoH1 = pageSeoData.value?.seo.h1.trim();

    if (seoH1) return seoH1;

    const nearbyH1 = nearbyData.value?.seo.seo.h1.trim();

    if (nearbyH1) return nearbyH1;

    const cityNameGenitive = nearbyData.value?.name_genitive.trim();

    return cityNameGenitive ? `Куда поехать на выходные из ${cityNameGenitive}` : 'Куда поехать на выходные';
  });

  const loadNearbyData = async () => {
    void trackEvent('around.pageview');

    if (!citySlug.value) return;

    try {
      nearbyLoading.value = true;
      nearbyError.value = null;
      nearbyNotFound.value = false;
      pageSeoData.value = null;

      const [nearbyResponse, cityResponse, cityTagsResponse, seoResponse] = await Promise.all([
        getNearbyCities({ path: { slug: citySlug.value } }),
        getCityDetail({ path: { slug: citySlug.value } }),
        getCityTags({ query: { city__slug: citySlug.value, page_size: 1000, sorting: '-popularity' } }),
        getCitiesAroundSeo({ query: { ids: citySlug.value } }).catch(() => null),
      ]);

      // Любой не-200 ответ nearby-cities => показываем 404-шаблон вместо контента
      const nearbyStatus = nearbyResponse.response.status;

      if (nearbyResponse.error || (nearbyStatus && nearbyStatus !== 200)) {
        nearbyNotFound.value = true;
        return;
      }

      nearbyData.value = nearbyResponse.data;
      cityDetail.value = cityResponse.data ?? null;
      cityTagsData.value = cityTagsResponse.data?.results ?? [];
      pageSeoData.value = seoResponse?.data ?? null;
    } catch (err) {
      nearbyNotFound.value = true;
      nearbyError.value = err instanceof Error ? err.message : 'Произошла ошибка при загрузке данных';
    } finally {
      nearbyLoading.value = false;
    }
  };

  onMounted(loadNearbyData);

  useHead(
    computed(() => {
      const seo = nearbyData.value?.seo.seo;

      if (!seo) {
        return {};
      }

      const { title, description, canonical_url, noindex } = seo;

      return {
        title,
        meta: [
          {
            name: 'description',
            content: description,
          },
          {
            name: 'robots',
            content: noindex ? 'noindex, nofollow' : 'index, follow',
          },
          {
            property: 'og:title',
            content: title,
          },
          {
            property: 'og:description',
            content: description,
          },
        ],
        link: [
          {
            rel: 'canonical',
            href: canonical_url,
          },
        ],
      };
    }),
  );

  const getSightExperiencesUrl = (sightCitySlug?: string, cityName?: string, sightSlug?: string) => {
    const normalizedCity = sightCitySlug || getCurrentCitySlugFromUrl() || cityName;

    if (!normalizedCity || !sightSlug) return '';

    return `/experience/${encodeURIComponent(normalizedCity)}/${encodeURIComponent(sightSlug)}`;
  };

  const showAllSightExperiencesUrl = computed(() => {
    const sight = activeSight.value;
    if (!sight) return '';

    if (activeSightShowAllUrl.value) return activeSightShowAllUrl.value;

    return getSightExperiencesUrl(activeSightCity.value.slug, activeSightCity.value.name_ru, sight.slug);
  });

  const seoCitySlug = computed(() => citySlug.value);
  const seoCityNameRu = computed(() => cityDetail.value?.name_ru || defaultSightCity.name_ru || 'Москва');

  // ContextGeoSubject
  const seoDefaultSoguFields = computed<ContextGeoSubject>(() => ({
    soguStamp: {
      subject_geo_id: 1,
      subject_geo_name: seoCityNameRu.value,
      subject_geo_type: 'city',
    },
  }));

  // string (может содержать HTML) — берём из ответа API, иначе фолбэк
  const seoText = computed(() => nearbyData.value?.seo.seo.text ?? '');

  // SeoDataFaq — реальный FAQ из nearby-cities BFF (nearbyData.seo.faq)
  const seoFaq = computed<SeoDataFaq>(() => (nearbyData.value?.seo.faq as SeoDataFaq | undefined) ?? {});

  // CityInfo
  const seoSlugInfo = computed<SeoCityInfo>(() => ({
    id: 1,
    name_ru: seoCityNameRu.value,
    slug: seoCitySlug.value,
    about_obj_phrase: '',
    in_obj_phrase: nearbyData.value?.in_obj_phrase,
    from_obj_phrase: '',
    across_obj_phrase: '',
    experience_count: 120,
    excursions_count: 120,
    tours_count: 0,
    guides_count: 0,
    url: `/experience/${seoCitySlug.value}/`,
  }));

  // Счётчик экскурсий у темы (как в SeoTags: если включены туры — суммируем с турами)
  const getTagExcursionsCount = (tag: ApiCityTag): number => {
    const excursions = tag.counters?.excursions_count ?? 0;

    return tag.flags?.is_tours_included ? excursions + (tag.counters?.tours_count ?? 0) : excursions;
  };

  // CityTag[] — реальные темы экскурсий из /api/web/v1/citytags/ (results), без тем с нулевым счётчиком
  const seoTags = computed<CityTag[]>(() => cityTagsData.value.filter((tag) => getTagExcursionsCount(tag) > 2));
</script>

<style scoped lang="scss">
  .sight-modal-demo {
    margin-bottom: var(--alias-space-24);
  }

  .mosaic-block {
    padding-top: 0;
  }

  .mosaic {
    margin-top: var(--alias-space-48, 48px);

    @include tablet {
      margin-top: var(--alias-space-40, 40px);
    }

    @include mobile {
      margin-top: var(--alias-space-32, 32px);
    }
  }

  .city-block {
    scroll-margin-top: 80px;
  }

  .seo-block-wrap {
    --font-family-base: var(--ffms);
    --global-typography-family-base: var(--ffms);

    margin-top: var(--alias-space-48);
    font-family: var(--ffms);
  }
</style>
